import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
  stripHtml,
} from "../../scripts/helperScripts";
import FormInput from "../assessment2/common/FormInput";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormCheckbox,
  FormRadioButton,
  FormTextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getProgressNotes } from "../services/soar";
import { getSchema } from "./joi/validation";

function KY1294A() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [progressNotes, setProgressNotes] = useState([]);
  const [progressNotesLoadStatus, setProgressNotesLoadStatus] = useState({});

  useEffect(() => {
    if (progressNotesLoadStatus.loaded || progressNotesLoadStatus.loading) {
      return;
    }

    const loadProgressNotes = async () => {
      setProgressNotesLoadStatus({ loading: true });
      const response = await getProgressNotes(clientid);

      if (!isMountedRef.current) {
        return;
      }

      const notes = response.filter(
        (progressNote) =>
          getFieldName(progressNote["rs.ccx_serviceitem"]) === "CAP"
      );

      setProgressNotes(notes);
      setProgressNotesLoadStatus({ loaded: true });
    };

    loadProgressNotes();
  }, [progressNotesLoadStatus, clientid]);

  const handleContactDateChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    const progressNote = progressNotes.filter((progressNote) =>
      moment(getFieldDate(progressNote["rs.ccx_starttime"])).isSame(
        moment(input.value)
      )
    )[0];

    if (!progressNote) {
      return;
    }

    obj.CAPNotes["@value"] = stripHtml(progressNote.ccx_note);
    setData(obj);
  };

  const getDivision = useCallback(() => {
    if (
      !getFieldValue(data.SelectedProgram) ||
      !clientData?.programs?.length ||
      clientData.programs.length === 0
    ) {
      return "";
    }

    const selectedProgramInfo = clientData.programOptions?.filter(
      (p) => p._id === getFieldValue(data.SelectedProgram)
    )[0];

    if (!selectedProgramInfo) {
      return "";
    }

    return selectedProgramInfo.name.split(" - ")[0];
  }, [data, clientData]);

  const handleDateOfApprovalChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    const dateOfApproval = moment(input.value);

    obj.DateOfReview["@value"] = dateOfApproval
      .add(6, "M")
      .format(moment.HTML5_FMT.DATE);
    setData(obj);

    const errs = { ...errors };
    delete errs.DateOfReview;
    setErrors(errs);
  };

  const contactLocationOptions = [
    {
      _id: "Child/Youth Placement Location",
      name: "Child/Youth Placement Location",
    },
    {
      _id: "Hospital/CSU - Mental Health",
      name: "Hospital/CSU - Mental Health",
    },
    {
      _id: "Community Outpatient Treatment",
      name: "Community Outpatient Treatment",
    },
    {
      _id: "Jail/Detention",
      name: "Jail/Detention",
    },
    {
      _id: "ER/Hospital - Medical",
      name: "ER/Hospital - Medical",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  ////UI
  return (
    <div style={{ fontFamily: "Segoe UI" }}>
      <div className="clearfix">
        <div className="float-start mb-2">
          <BackIcon />
        </div>
        <div className="float-end mb-2 d-print-none">
          <a
            href="https://safy365.sharepoint.com/:w:/s/Kentucky/EXsclM3AiChLkMyNTjKoSYQBQBCo2jc_chxJ-OqQfwNVgg"
            target="_blank"
            rel="noreferrer"
          >
            Forms Information
          </a>
        </div>
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          hideHeaderOnPrint={true}
          letterhead={
            <div className="w-100 text-center fw-bold">
              DPP-1294A
              <br />
              R. 7/16
              <br />
              Commonwealth of Kentucky
              <br />
              Cabinet for Health and Family Services
              <br />
              Department of Community Based Services
              <br />
              Division of Protection and Permanency
              <br />
              <br />
              Rehabilitative Services Monthly Contact Report
            </div>
          }
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          printStyle={`body {
            font-family: "Segoe UI";
            font-size: .75rem;
          }`}
        />
        <div className="py-2 border-bottom border-dark d-print-none">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                This form must be completed and entered into the PCC tracking
                module by the 4th of each month.
              </p>
              <div className="row">
                <div className="col-6">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">MONTH ENDING:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="MonthEnding"
                          error={errors.MonthEnding}
                          value={data.MonthEnding["@value"]}
                          onChange={handleChange}
                          identical={compare("MonthEnding")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-7">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">DCBS CASE MANAGER:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="CaseManager"
                          error={errors.CaseManager}
                          value={data.CaseManager["@value"]}
                          onChange={handleChange}
                          identical={compare("CaseManager")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="d-flex lh-12 w-auto">
                    <span className="me-2">CHILD NAME:</span>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        {clientData &&
                          clientData.client &&
                          clientData.client.fullName}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-5">
                  <div className="d-flex lh-12 w-auto">
                    <span className="me-2">DOB:</span>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        {clientData &&
                          clientData.client &&
                          getFieldDate(clientData.client.dob)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="d-flex lh-12 mt-3 w-auto">
                    <div className="me-2">SSN NUMBER:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="SSNumber"
                          error={errors.SSNumber}
                          value={data.SSNumber["@value"]}
                          onChange={handleChange}
                          identical={compare("SSNumber")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-7">
                  <div className="d-flex lh-12 mt-3 w-auto">
                    <div className="me-2">PROVIDER/FACILITY:</div>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        SAFY of {getDivision(data)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col-7">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">
                      Date of Current DPP-1293 Approval:
                    </div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="DateOfApproval"
                          error={errors.DateOfApproval}
                          value={data.DateOfApproval["@value"]}
                          onChange={handleDateOfApprovalChange}
                          identical={compare("DateOfApproval")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-7">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">Date of Next Six Month Review:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="DateOfReview"
                          error={errors.DateOfReview}
                          value={data.DateOfReview["@value"]}
                          onChange={handleChange}
                          identical={compare("DateOfReview")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div className="d-flex">
                <span className="me-2">
                  MONTHLY FACE-TO-FACE CONTACT INFORMATION
                </span>
                <span className="d-print-none">
                  (
                  <Link
                    to={`/client/${clientid}?tab=documentation`}
                    target="_blank"
                  >
                    Documentation
                  </Link>
                  )
                </span>
              </div>
              <br />
              <ol>
                <li>
                  <div className="d-flex lh-12 w-50">
                    <div className="me-2">Date of contact:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="ContactDate"
                          error={errors.ContactDate}
                          value={data.ContactDate["@value"]}
                          onChange={handleContactDateChange}
                          identical={compare("ContactDate")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <FormTextArea
                    name="CAPNotes"
                    error={errors.CAPNotes}
                    value={getFieldValue(data.CAPNotes)}
                    onChange={handleChange}
                    identical={compare("CAPNotes")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  Location of contact (<b>check only one</b>)
                  <div className="container border mx-2 my-4">
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">
                        <FormRadioButton
                          name="ContactLocation"
                          error={errors.ContactLocation}
                          value={data.ContactLocation["@value"]}
                          options={[contactLocationOptions[0]]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsDivClassName="ps-0"
                        />
                      </div>
                      <div className="col py-2">
                        <FormRadioButton
                          name="ContactLocation"
                          error={errors.ContactLocation}
                          value={data.ContactLocation["@value"]}
                          options={[contactLocationOptions[1]]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsDivClassName="ps-0"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">
                        <FormRadioButton
                          name="ContactLocation"
                          error={errors.ContactLocation}
                          value={data.ContactLocation["@value"]}
                          options={[contactLocationOptions[2]]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsDivClassName="ps-0"
                        />
                      </div>
                      <div className="col py-2">
                        <FormRadioButton
                          name="ContactLocation"
                          error={errors.ContactLocation}
                          value={data.ContactLocation["@value"]}
                          options={[contactLocationOptions[3]]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsDivClassName="ps-0"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col py-2 border-end">
                        <FormRadioButton
                          name="ContactLocation"
                          error={errors.ContactLocation}
                          value={data.ContactLocation["@value"]}
                          options={[contactLocationOptions[4]]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsDivClassName="ps-0"
                        />
                      </div>
                      <div className="col py-2">
                        <FormRadioButton
                          name="ContactLocation"
                          error={errors.ContactLocation}
                          value={data.ContactLocation["@value"]}
                          options={[contactLocationOptions[5]]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsDivClassName="ps-0"
                        />
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  Service activity conducted (<b>check all that apply</b>)
                  <br />
                  <div className="container border mx-2 my-4">
                    <div className="row border-bottom">
                      <div className="col pt-2 border-end">
                        <FormCheckbox
                          name="Evaluation"
                          label="Child/Youth Evaluation"
                          checked={data.Evaluation["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                      <div className="col pt-2 border-end">
                        <FormCheckbox
                          name="IndependentLiving"
                          label="Independent and Transitional Living"
                          checked={data.IndependentLiving["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                      <div className="col pt-2">
                        <FormCheckbox
                          name="Placement"
                          label="Placement"
                          checked={data.Placement["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom">
                      <div className="col pt-2 border-end">
                        <FormCheckbox
                          name="Counseling"
                          label="Counseling Services"
                          checked={data.Counseling["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                      <div className="col pt-2 border-end">
                        <FormCheckbox
                          name="Ongoing"
                          label="Ongoing Services"
                          checked={data.Ongoing["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                      <div className="col pt-2">
                        <FormCheckbox
                          name="Transportation"
                          label="Transportation"
                          checked={data.Transportation["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col pt-2 border-end">
                        <FormCheckbox
                          name="FamilyMeeting"
                          label="Family Team Meeting"
                          checked={data.FamilyMeeting["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                      <div className="col pt-2 border-end">
                        <FormCheckbox
                          name="Visitation"
                          label="Parent, Child / Youth, Sibling Visitation"
                          checked={data.Visitation["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-1"
                        />
                      </div>
                      <div className="col"></div>
                    </div>
                  </div>
                </li>
                <li>
                  <p>
                    Description of service activity including but not limited to
                    verification of Lifebook development, review of treatment
                    plan (including supervision plan), review of medical
                    passport, review of educational or developmental progress,
                    and review of visitation agreement or permanency plan.
                  </p>
                  <p>
                    Document specific information regarding the following
                    components of the treatment plan
                  </p>
                  <ol type="a">
                    <li>
                      <FormTextArea
                        name="TreatmentPlan"
                        label="Mental health/treatment plan"
                        error={errors.TreatmentPlan}
                        value={data.TreatmentPlan["@value"]}
                        onChange={handleChange}
                        identical={compare("TreatmentPlan")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="AttachmentIssues"
                        label="Attachment issues"
                        error={errors.AttachmentIssues}
                        value={data.AttachmentIssues["@value"]}
                        onChange={handleChange}
                        identical={compare("AttachmentIssues")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        placeholder="Include Lifebook development, attachment to biological parents, siblings, kinship supports, and other significant supports in their life..."
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="Education"
                        label="Education"
                        error={errors.Education}
                        value={data.Education["@value"]}
                        onChange={handleChange}
                        identical={compare("Education")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        placeholder="Include address, academic, and behavioral perfomance, and extracurricular school-related activities. Reference how information was obtained..."
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="Permanency"
                        label="Permanency"
                        error={errors.Permanency}
                        value={data.Permanency["@value"]}
                        onChange={handleChange}
                        identical={compare("Permanency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="MedicalHealth"
                        label="Medical/physical health"
                        error={errors.MedicalHealth}
                        value={data.MedicalHealth["@value"]}
                        onChange={handleChange}
                        identical={compare("MedicalHealth")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        placeholder="Include physical, dental, and vision providers, addresses, last appointments, and upcoming appointments. If applicable, include specialist information as well..."
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="IndependentLivingSkills"
                        label={
                          <>
                            Independent living skills{" "}
                            <i
                              className="fa fa-info-circle text-primary"
                              title="Ex: Four-year-old is able to pick up toys when asked, 12-year-old can prepare their own lunch, etc."
                            />
                          </>
                        }
                        error={errors.IndependentLivingSkills}
                        value={data.IndependentLivingSkills["@value"]}
                        onChange={handleChange}
                        identical={compare("IndependentLivingSkills")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        placeholder="Include IL skills for all ages and developmental levels..."
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="Compliance"
                        label="Compliance with court orders (if applicable)"
                        error={errors.Compliance}
                        value={data.Compliance["@value"]}
                        onChange={handleChange}
                        identical={compare("Compliance")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </li>
                    <li>
                      <FormTextArea
                        name="Safety"
                        label="Safety and supervision"
                        error={errors.Safety}
                        value={data.Safety["@value"]}
                        onChange={handleChange}
                        identical={compare("Safety")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </li>
                  </ol>
                </li>
              </ol>
              <br />
              <FormInput
                name="CompletedBy"
                label="NAME AND TITLE OF PERSON COMPLETING FORM"
                error={errors.CompletedBy}
                value={data.CompletedBy["@value"]}
                onChange={handleChange}
                identical={compare("CompletedBy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="w-100 border-bottom border-dark"
                printClassName="ps-1"
              />
              <div className="d-flex mt-2">
                <div className="me-2">SIGNATURE:</div>
                <div className="flex-fill border-bottom border-dark">
                  {signatures.filter((sig) => sig.signatureType === "Provider")
                    .length > 0 ? (
                    <>
                      {signatures
                        .filter((sig) => sig.signatureType === "Provider")
                        .map((s) => {
                          return (
                            <div key={moment(s.datetime).unix()}>
                              {`${s.name}${
                                s.signatureRole ? ` ${s.signatureRole}` : ""
                              }`}
                            </div>
                          );
                        })}
                    </>
                  ) : null}
                </div>
              </div>
              <FormInput
                name="SupervisorName"
                label="SUPERVISOR'S NAME AND SIGNATURE (IF REQUIRED)"
                error={errors.SupervisorName}
                value={data.SupervisorName["@value"]}
                onChange={handleChange}
                identical={compare("SupervisorName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="w-100 border-bottom border-dark mt-2"
                printClassName="ps-1"
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KY1294A;
